import React, { useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import reportApi from "services/reportApi";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Styled from "./styles";
import MaintenanceScreen from "./components/maintenanceScreen";

const ERROR_MESSAGE =
  "Falha ao carregar dados do relatório. Por favor, tente novamente.";

interface BiEmbedResponse {
  name: string;
  embedUrl: string;
  accessToken: string;
}

interface BiEmbedListResponse {
  data?: BiEmbedResponse[];
}

const ReportsSales = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const powerbiRef = useRef<{ service: any; models: any } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let container: HTMLDivElement | null = null;
    const abort = new AbortController();

    const fetchAndEmbed = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!powerbiRef.current) {
          const pbi = await import("powerbi-client");
          powerbiRef.current = {
            service: new pbi.service.Service(
              pbi.factories.hpmFactory,
              pbi.factories.wpmpFactory,
              pbi.factories.routerFactory,
            ),
            models: pbi.models,
          };
        }

        const { data } = await reportApi.get<BiEmbedListResponse>(
          "/v1/dealer/bi/reports",
          {
            timeout: 45_000,
            signal: abort.signal,
          },
        );
        if (cancelled) return;

        const report = data?.data?.[0];
        if (!report) {
          throw new Error(ERROR_MESSAGE);
        }

        container = containerRef.current;
        if (!container || !powerbiRef.current) return;

        const config = {
          type: "report",
          tokenType: powerbiRef.current.models.TokenType.Embed,
          embedUrl: report.embedUrl,
          accessToken: report.accessToken,
          settings: {
            filterPaneEnabled: false,
            navContentPaneEnabled: true,
          },
        };

        powerbiRef.current.service.embed(container, config);
      } catch (err: unknown) {
        if (cancelled) return;
        const message =
          (err as { response?: { data?: { description?: string } } })?.response
            ?.data?.description ||
          (err as Error)?.message ||
          ERROR_MESSAGE;
        setError(String(message));
        toast(message, { type: "error", autoClose: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAndEmbed();

    return () => {
      cancelled = true;
      abort.abort();
      if (container && powerbiRef.current) {
        try {
          powerbiRef.current.service.reset(container);
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  return (
    <Styled.Container>
      <Styled.Content>
        {loading && (
          <Styled.LoadingWrapper>
            <CircularProgress />
          </Styled.LoadingWrapper>
        )}
        {error && !loading && (
          <MaintenanceScreen open={!!error && !loading} onClose={() => {}} />
        )}
        {!error && (
          <Styled.EmbedWrapper
            ref={containerRef}
            style={{ display: loading ? "none" : "block" }}
          />
        )}
      </Styled.Content>
    </Styled.Container>
  );
};

export default ReportsSales;
